import React, { useState } from 'react';
import { DollarSign, Copy, Trash2, TrendingUp } from 'lucide-react';

const CommissionCalculatorTool = () => {
  const [mode, setMode] = useState('basic');
  const [saleAmount, setSaleAmount] = useState('');
  const [commissionRate, setCommissionRate] = useState('');
  const [baseSalary, setBaseSalary] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const calculate = () => {
    const sale = parseFloat(saleAmount);
    const rate = parseFloat(commissionRate);
    const base = parseFloat(baseSalary) || 0;
    const target = parseFloat(targetAmount);

    if (mode === 'basic') {
      if (isNaN(sale) || isNaN(rate)) {
        setResult(null);
        return;
      }
      const commission = (sale * rate) / 100;
      setResult({
        commission: commission,
        total: commission + base,
        breakdown: [
          { label: 'Sale Amount', value: `$${sale.toLocaleString()}` },
          { label: 'Commission Rate', value: `${rate}%` },
          { label: 'Commission Earned', value: `$${commission.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
          ...(base > 0 ? [{ label: 'Base Salary', value: `$${base.toLocaleString()}` }] : []),
          ...(base > 0 ? [{ label: 'Total Earnings', value: `$${(commission + base).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` }] : [])
        ]
      });
    } else if (mode === 'reverse') {
      if (isNaN(target) || isNaN(rate)) {
        setResult(null);
        return;
      }
      const salesNeeded = (target / rate) * 100;
      setResult({
        commission: target,
        total: salesNeeded,
        breakdown: [
          { label: 'Target Commission', value: `$${target.toLocaleString()}` },
          { label: 'Commission Rate', value: `${rate}%` },
          { label: 'Sales Needed', value: `$${salesNeeded.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` }
        ]
      });
    } else if (mode === 'tiered') {
      if (isNaN(sale)) {
        setResult(null);
        return;
      }
      // Tiered commission: 5% up to $10k, 7% for $10k-$50k, 10% above $50k
      let commission = 0;
      const tiers = [];
      
      if (sale <= 10000) {
        commission = sale * 0.05;
        tiers.push({ range: '$0 - $10,000', rate: '5%', amount: commission });
      } else if (sale <= 50000) {
        const tier1 = 10000 * 0.05;
        const tier2 = (sale - 10000) * 0.07;
        commission = tier1 + tier2;
        tiers.push({ range: '$0 - $10,000', rate: '5%', amount: tier1 });
        tiers.push({ range: '$10,001 - $50,000', rate: '7%', amount: tier2 });
      } else {
        const tier1 = 10000 * 0.05;
        const tier2 = 40000 * 0.07;
        const tier3 = (sale - 50000) * 0.10;
        commission = tier1 + tier2 + tier3;
        tiers.push({ range: '$0 - $10,000', rate: '5%', amount: tier1 });
        tiers.push({ range: '$10,001 - $50,000', rate: '7%', amount: tier2 });
        tiers.push({ range: '$50,001+', rate: '10%', amount: tier3 });
      }

      setResult({
        commission: commission,
        total: commission,
        tiers: tiers,
        breakdown: [
          { label: 'Sale Amount', value: `$${sale.toLocaleString()}` },
          { label: 'Total Commission', value: `$${commission.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
          { label: 'Effective Rate', value: `${((commission / sale) * 100).toFixed(2)}%` }
        ]
      });
    }
  };

  const copyResult = () => {
    if (result) {
      navigator.clipboard.writeText(result.commission.toFixed(2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const clearAll = () => {
    setSaleAmount('');
    setCommissionRate('');
    setBaseSalary('');
    setTargetAmount('');
    setResult(null);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-2">
        {[
          { key: 'basic', label: 'Basic Commission' },
          { key: 'reverse', label: 'Reverse Calculator' },
          { key: 'tiered', label: 'Tiered Commission' }
        ].map((m) => (
          <button
            key={m.key}
            onClick={() => { setMode(m.key); setResult(null); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              mode === m.key
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        {mode === 'basic' && (
          <>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Sale Amount ($)
                </label>
                <input
                  type="number"
                  value={saleAmount}
                  onChange={(e) => setSaleAmount(e.target.value)}
                  placeholder="e.g., 50000"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Commission Rate (%)
                </label>
                <input
                  type="number"
                  value={commissionRate}
                  onChange={(e) => setCommissionRate(e.target.value)}
                  placeholder="e.g., 5"
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Base Salary (optional)
              </label>
              <input
                type="number"
                value={baseSalary}
                onChange={(e) => setBaseSalary(e.target.value)}
                placeholder="e.g., 3000"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
              />
            </div>
          </>
        )}

        {mode === 'reverse' && (
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Target Commission ($)
              </label>
              <input
                type="number"
                value={targetAmount}
                onChange={(e) => setTargetAmount(e.target.value)}
                placeholder="e.g., 5000"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Commission Rate (%)
              </label>
              <input
                type="number"
                value={commissionRate}
                onChange={(e) => setCommissionRate(e.target.value)}
                placeholder="e.g., 5"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
              />
            </div>
          </div>
        )}

        {mode === 'tiered' && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Sale Amount ($)
            </label>
            <input
              type="number"
              value={saleAmount}
              onChange={(e) => setSaleAmount(e.target.value)}
              placeholder="e.g., 75000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
            />
            <div className="mt-3 p-3 bg-gray-900 rounded-lg text-sm text-gray-400">
              <div className="font-medium text-gray-300 mb-2">Tier Structure:</div>
              <div>$0 - $10,000: 5%</div>
              <div>$10,001 - $50,000: 7%</div>
              <div>$50,001+: 10%</div>
            </div>
          </div>
        )}
      </div>

      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
      >
        <TrendingUp size={18} />
        Calculate Commission
      </button>

      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-400 mb-1">
                {mode === 'reverse' ? 'Sales Needed' : 'Commission Earned'}
              </div>
              <div className="text-3xl font-bold text-green-400">
                ${(mode === 'reverse' ? result.total : result.commission).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <button
              onClick={copyResult}
              className={`p-2 rounded transition-colors ${
                copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
              }`}
            >
              <Copy size={18} />
            </button>
          </div>

          {result.tiers && (
            <div className="border-t border-gray-700 pt-4">
              <div className="text-sm font-medium text-gray-300 mb-2">Tier Breakdown:</div>
              {result.tiers.map((tier, idx) => (
                <div key={idx} className="flex justify-between text-sm text-gray-400 py-1">
                  <span>{tier.range} @ {tier.rate}</span>
                  <span>${tier.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              ))}
            </div>
          )}

          <div className="border-t border-gray-700 pt-4">
            {result.breakdown.map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm py-1">
                <span className="text-gray-400">{item.label}</span>
                <span className="text-white font-medium">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Common Commission Rates</h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-400">
          <div>
            <div className="text-gray-500 mb-1">Real Estate</div>
            <div>2.5% - 6%</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Insurance</div>
            <div>5% - 20%</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Retail Sales</div>
            <div>1% - 10%</div>
          </div>
        </div>
      </div>

      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default CommissionCalculatorTool;
